import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Settings as SettingsIcon, Moon, Sun, User, Shield, LogOut } from "lucide-react";
import { useAuth, useTheme } from "../App";
import { useNavigate } from "react-router-dom";

const Settings = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Layout title="Settings" subtitle="Manage your account and preferences">
      <div className="max-w-2xl space-y-6">
        {/* Profile */}
        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
              <User className="w-5 h-5" />
              Profile
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Avatar className="w-16 h-16">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-primary/10 text-primary text-xl">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div>
                <h3 className="text-xl font-semibold">{user?.name}</h3>
                <p className="text-muted-foreground">{user?.email}</p>
                <Badge variant="outline" className="mt-2 bg-primary/10 text-primary border-primary/30">
                  <Shield className="w-3 h-3 mr-1" />
                  {user?.role}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Appearance */}
        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
              {theme === "dark" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              Appearance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Dark Mode</p>
                <p className="text-sm text-muted-foreground">
                  Switch between light and dark themes
                </p>
              </div>
              <Switch
                checked={theme === "dark"}
                onCheckedChange={toggleTheme}
                data-testid="dark-mode-toggle"
              />
            </div>
          </CardContent>
        </Card>

        {/* Role Permissions */}
        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Your Permissions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm">View leads and dashboard</span>
                <Badge variant="outline" className="bg-green-500/10 text-green-500">
                  Allowed
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Upload leads</span>
                <Badge
                  variant="outline"
                  className={
                    user?.role !== "Viewer"
                      ? "bg-green-500/10 text-green-500"
                      : "bg-red-500/10 text-red-500"
                  }
                >
                  {user?.role !== "Viewer" ? "Allowed" : "Not Allowed"}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Export data</span>
                <Badge
                  variant="outline"
                  className={
                    user?.role !== "Viewer"
                      ? "bg-green-500/10 text-green-500"
                      : "bg-red-500/10 text-red-500"
                  }
                >
                  {user?.role !== "Viewer" ? "Allowed" : "Not Allowed"}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Edit leads</span>
                <Badge
                  variant="outline"
                  className={
                    user?.role !== "Viewer"
                      ? "bg-green-500/10 text-green-500"
                      : "bg-red-500/10 text-red-500"
                  }
                >
                  {user?.role !== "Viewer" ? "Allowed" : "Not Allowed"}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Manage users</span>
                <Badge
                  variant="outline"
                  className={
                    user?.role === "Admin"
                      ? "bg-green-500/10 text-green-500"
                      : "bg-red-500/10 text-red-500"
                  }
                >
                  {user?.role === "Admin" ? "Allowed" : "Not Allowed"}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Delete leads</span>
                <Badge
                  variant="outline"
                  className={
                    user?.role === "Admin"
                      ? "bg-green-500/10 text-green-500"
                      : "bg-red-500/10 text-red-500"
                  }
                >
                  {user?.role === "Admin" ? "Allowed" : "Not Allowed"}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logout */}
        <Card className="border-border/60">
          <CardContent className="p-4">
            <Button
              variant="destructive"
              onClick={handleLogout}
              className="w-full"
              data-testid="settings-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Settings;
